import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';
import type React from 'react';
import { useState } from 'react';
import type { FreeTrialState } from '../api/freetrial/types';
import { useUpdateFreeTrialEligibilityAction } from './useUpdateFreeTrialEligibilityAction';

type FreeTrialTerminationDialogProps = {
    show: boolean;
    freeTrialState: FreeTrialState;
    statusLabel: React.ReactNode;
    onClose: () => void;
};

export function FreeTrialTerminationDialog(props: FreeTrialTerminationDialogProps) {
    const { show, freeTrialState, statusLabel, onClose } = props;
    const [terminationReason, setTerminationReason] = useState('');
    const { updateFreeTrialEligibility, isLoading: isSubmitting } = useUpdateFreeTrialEligibilityAction();

    const resetDialog = () => {
        setTerminationReason('');
    };

    const handleClose = () => {
        resetDialog();
        onClose();
    };

    const handleConfirmTermination = async () => {
        if (terminationReason.trim() === '') {
            return;
        }

        const wasSuccessful = await updateFreeTrialEligibility({
            accountId: freeTrialState.accountId,
            status: 'ineligible',
            reason: terminationReason.trim(),
        });

        if (wasSuccessful) {
            handleClose();
        }
    };

    return (
        <ConfirmationDialog
            show={show}
            title='Confirm free trial termination'
            content={
                <div>
                    <p>Are you sure you want to terminate the customer's free trial?</p>
                    <p>
                        Please note that this action will revoke free trial eligibility and <b>immediately </b>
                        cancel all active free trial services associated with the customer account.
                    </p>
                    <table className='table table-hover table-head-filled table-bordered'>
                        <thead>
                            <tr>
                                <th>AccountId</th>
                                <th>Name</th>
                                <th>Free Trial Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{freeTrialState.accountId}</td>
                                <td>{freeTrialState.accountName}</td>
                                <td>{statusLabel}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div className='form-group margin-bottom-10'>
                        <label htmlFor='freeTrialTerminationReason'>
                            <h5>Reason or ticket number*</h5>
                        </label>
                        <ClearableInput
                            id='freeTrialTerminationReason'
                            value={terminationReason}
                            onChange={(newValue: string) => setTerminationReason(newValue)}
                            placeholder='Enter reason or ticket number'
                        />
                    </div>
                </div>
            }
            cancelButtonText='Abort'
            confirmButtonText={isSubmitting ? 'Terminating...' : 'Terminate free trial'}
            disableConfirm={terminationReason.trim() === '' || isSubmitting}
            onClickCancel={handleClose}
            onClickConfirm={handleConfirmTermination}
        />
    );
}
